import { MinMaxFilter } from '@appTypes/FilterTypes';
import { Icon } from '@components/style';
import { DropdownWrapper } from '@components/style/DropdownWrapper';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { convertDate, dateDifference, formatDate, formatDateWithUtc } from '@utils/DateUtils';
import { FC, useEffect, useRef, useState } from 'react';
import { useOutsideClickDetection } from '../../../../hooks';
import DropdownSelect from '../dropdown-select/DropdownSelect';
import CalenderWrapper from './CalenderWrapper';

export type DatePickerRangeProps = {
  selectedValue: MinMaxFilter<number>;
  possibleRange: MinMaxFilter<number>;
  placeholder: string;
  maxDate?: Date;
  minDate?: Date;
  defaultFromDate?: Date;
  defaultToDate?: Date;
  defaultValue?: MinMaxFilter<number>;
  onChange: (value: MinMaxFilter<number>) => void;
  onOpen?: () => void;
  onClose?: () => void;
  type: 'am-label' | 'am-dropdown';
  cssClass?: string;
};

const DatePickerRange: FC<DatePickerRangeProps> = ({
  selectedValue,
  possibleRange,
  placeholder,
  minDate,
  maxDate,
  defaultFromDate = null,
  defaultToDate = null,
  defaultValue = { min: null, max: null },
  onChange,
  onOpen,
  onClose,
  type,
  cssClass = '',
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [isChange, setIsChange] = useState<boolean>(false);
  const [value, setValue] = useState<MinMaxFilter<number>>(defaultValue);
  const [fromDate, setFromDate] = useState<Date>(defaultFromDate);
  const [toDate, setToDate] = useState<Date>(defaultToDate);
  const [hasInvalidRange, setHasInvalidRange] = useState<boolean>(false);
  const { isMobileDevice } = useCheckDevice();

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const label = value
    ? value.min !== null || value.max !== null
      ? `${value.max === null ? 'N/A' : formatDate(fromDate)} - ${value.min === null ? 'N/A' : formatDate(toDate)}`
      : null
    : null;

  const handleMouseEvent = (event: any): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  const getMinMax = (date: Date) => {
    const { days } = dateDifference(date);
    return days;
  };

  const onDateChange = (date: Date, isStartDate: boolean, value?: MinMaxFilter<number>): void => {
    let updatedValue: MinMaxFilter<number> = { min: value?.min, max: value?.max };

    if (isStartDate) {
      updatedValue.max = getMinMax(date);
      setFromDate(date);
      validateDateRange(date, toDate);
    } else {
      updatedValue.min = getMinMax(date);
      setToDate(date);
      validateDateRange(fromDate, date);
    }

    setValue(updatedValue);
    setIsChange(true);
  };

  const validateDateRange = (fromDateValue: Date, toDateValue: Date): void => {
    const minDate = convertDate(fromDateValue).getTime();
    const maxDate = convertDate(toDateValue).getTime();

    if (minDate > maxDate) {
      setHasInvalidRange(true);
    } else {
      setHasInvalidRange(false);
    }
  };

  const reset = (): void => {
    if (value?.max != defaultValue?.max || value?.min !== defaultValue?.min) {
      setHasInvalidRange(false);
      setValue(defaultValue);
      setIsChange(true);
      setIsVisible(false);
    }
  };

  useEffect(() => {
    if (selectedValue === null) {
      setFromDate(null);
      setToDate(null);
      setValue(possibleRange);
      setHasInvalidRange(false);
    } else if (
      selectedValue?.min === defaultValue?.min &&
      selectedValue?.max === defaultValue?.max
    ) {
      setFromDate(defaultFromDate);
      setToDate(defaultToDate);
      setValue(selectedValue);
    } else {
      if (selectedValue?.max !== defaultValue?.max && !isChange) {
        const fromDate = new Date(
          formatDateWithUtc(
            new Date(new Date().setDate(new Date().getDate() - selectedValue?.max)),
          ),
        );
        setFromDate(fromDate);
      }
      if (selectedValue?.min !== defaultValue?.min && !isChange) {
        const toDate = new Date(
          formatDateWithUtc(
            new Date(new Date().setDate(new Date().getDate() - selectedValue?.min)),
          ),
        );
        setToDate(toDate);
      }
      setValue(selectedValue);
    }
    setIsChange(false);
  }, [selectedValue]);

  useEffect(() => {
    if (!isVisible && isChange && !hasInvalidRange) {
      onChange(value);
    }
  }, [isVisible, isChange]);

  useEffect(() => {
    if (hasInvalidRange) {
      setIsVisible(true);
    }
  }, [hasInvalidRange]);

  useEffect(() => {
    if (isVisible && onOpen) {
      onOpen();
    }
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible]);

  return (
    <DropdownWrapper
      isMobileDevice={isMobileDevice}
      data-testid='date-range-dropdown'
      ref={wrapperRef}
      flexDirection='row'
      className={cssClass}
    >
      <div className={`heap-filter-item ${type === 'am-dropdown' ? 'w-88' : ''}`}>
        <span
          data-testid='date-range-dropdown-header'
          onClick={() => setIsVisible(!isVisible)}
          onMouseDown={handleMouseEvent}
        >
          <DropdownSelect name={placeholder} bottomLabel={label} />
        </span>
        {isVisible && (
          <CalenderWrapper
            filterHeight={filterHeight}
            hasInvalidRange={hasInvalidRange}
            fromDate={fromDate}
            toDate={toDate}
            value={value}
            onDateChange={onDateChange}
            reset={reset}
            minDate={minDate}
            maxDate={maxDate}
            defaultValue={defaultValue}
          />
        )}
      </div>
      {type === 'am-dropdown' && (
        <div className='vertical-center'>
          <Icon type='dropdown' size={14} />
        </div>
      )}
    </DropdownWrapper>
  );
};

export default DatePickerRange;
